"use client";

import { useAuth } from "@/components/AuthContext";
import { InfiniteTestimonialScroll } from "@/components/paywall/InfiniteTestimonialScroll";
import { PaywallExperimentPricingCards } from "@/components/paywall/PaywallExperimentPricingCards";
import { PaywallSkeleton } from "@/components/paywall/PaywallSkeleton";
import { PricingCard } from "@/components/paywall/PricingCard";
import Link from "@/components/school/SchoolLink";
import { useUserPreferences } from "@/hooks/useLocalStorage";
import {
  exposePaywallGuidedAnchorExperiment,
  usePaywallGuidedAnchorExperiment,
} from "@/hooks/usePaywallGuidedAnchorExperiment";
import { useRevenueCatCheckoutAttempt } from "@/hooks/useRevenueCatCheckoutAttempt";
import { invalidateRevenueCatSubscriptionCache } from "@/hooks/useRevenueCatSubscription";
import { useRouter } from "@/hooks/useSchoolRouter";
import {
  trackBeginCheckout,
  trackPaywallView,
  trackRevenueCatConversionEvent,
} from "@/lib/analytics/gtm-datalayer";
import { normalizePaywallSource } from "@/lib/analytics/paywall-source";
import { trackEventWithAttribution, trackUpgradeIntent } from "@/lib/analytics/posthog-helpers";
import { buildOpenAIAdsPurchaseMetadata } from "@/lib/analytics/utm-tracker";
import { fastAuthCheck, resolvePaywallAuthGateDecision } from "@/lib/auth-utils";
import {
  PAYWALL_GUIDED_ANCHOR_EXPERIMENT_KEY,
  PAYWALL_GUIDED_ANCHOR_LAYOUTS,
  PAYWALL_GUIDED_ANCHOR_VARIANTS,
} from "@/lib/experiments/paywallGuidedAnchor";
import { getWebappSignupBeforeDemoAttributionProperties } from "@/lib/experiments/webappSignupBeforeDemo";
import { createLogger } from "@/lib/logger";
import {
  clearPendingPaywallRecovery,
  createPaywallRecoveryRecord,
  getReturnRouteCategory,
  sanitizeInternalReturnTo,
  updatePendingPaywallRecovery,
  writePendingPaywallRecovery,
} from "@/lib/paywall/recovery";
import {
  OfferMode,
  UserIntentSegment,
  classifyUserIntentFromStats,
  recommendedOfferModeForSegment,
} from "@/lib/paywall/user-intent";
import { buildCheckoutSubscriberAttributes } from "@/lib/revenuecat/checkout-attributes";
import {
  CHECKOUT_TELEMETRY_VERSION,
  CheckoutAttempt,
  CheckoutStage,
} from "@/lib/revenuecat/checkout-telemetry";
import {
  getOfferings,
  initializeRevenueCat,
  purchasePackage,
  setAttributes,
} from "@/lib/revenuecat/client";
import { Currency, PackageType, REVENUECAT_CONFIG } from "@/lib/revenuecat/config";
import { detectUserCurrency } from "@/lib/revenuecat/currency-detector";
import {
  findSelectableRevenueCatPackage,
  getComparableAnnualSavingsPercent,
  getFallbackPackagePriceValue,
  getPackageDisplayPricing,
  getRevenueCatPackageCurrency,
  getRevenueCatPackagePriceAmount,
} from "@/lib/revenuecat/package-pricing";
import { getPromoDisplayState } from "@/lib/revenuecat/promo-display";
import { getPaywallLoadErrorMessage } from "@/lib/revenuecat/user-facing-errors";
import { schoolCheckoutReturnTo, schoolHref } from "@/lib/school-routes";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { useSearchParams } from "next/navigation";
import { usePostHog } from "posthog-js/react";
import { Suspense, useEffect, useRef, useState } from "react";

const logger = createLogger("PaywallPage");

const guidedAnchorTestimonials = [
  {
    name: "Dutch Learner",
    role: "Netherlands/Germany",
    content:
      "Your app is awesome! It got me over the first hump to actually speak Dutch, because I was still too nervous to practice with native speakers. Now I use Dutch confidently with my colleagues in the Netherlands and with people at my store in Berlin. Your app really made the difference for my speaking!",
    rating: 5,
    location: "Berlin",
  },
  {
    name: "YouTube Reviewer",
    role: "Content Creator",
    content:
      "The UX is great - especially that you can try it out immediately and get the 'aha' moment fast. In my opinion, ChickyTutor is the AI language tutor with the most potential. The simplicity is amazing. It's like working with a tutor - you're picking things up pretty quickly.",
    rating: 4,
    location: "via YouTube",
  },
  {
    name: "Multi-Language Learner",
    role: "Mandarin/Japanese/Thai",
    content:
      "This is better than Duolingo in my opinion. This is if you're already someone who's knee-deep in languages and you're running out of apps to use because a lot of apps are built for beginners. This is a great start. It has so many languages. It's got Thai, Cantonese which is unheard of. Two thumbs up from me.",
    rating: 5,
    location: "Advanced Learner",
  },
];

function PaywallContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const paywallSource = normalizePaywallSource(searchParams?.get("source"));
  const { user, isLoading: authLoading } = useAuth();
  const { preferences, isInitialized: preferencesInitialized } = useUserPreferences();
  const posthog = usePostHog();
  const trackedPaywallViewOwnerRef = useRef<string | null>(null);
  const recoveryRecordOwnerRef = useRef<string | null>(null);

  const mode = searchParams?.get("mode");
  const isZenMode = mode === "zen";
  const paywallExperiment = usePaywallGuidedAnchorExperiment({
    enabled: !isZenMode && Boolean(process.env.NEXT_PUBLIC_POSTHOG_KEY),
    posthog,
    userId: user?.id,
  });
  const isGuidedAnchorTreatment =
    !isZenMode && paywallExperiment.variant === PAYWALL_GUIDED_ANCHOR_VARIANTS.guidedAnchor;
  const paywallLayout = PAYWALL_GUIDED_ANCHOR_LAYOUTS[paywallExperiment.variant];

  const checkout = useRevenueCatCheckoutAttempt(posthog);
  const [isInitializing, setIsInitializing] = useState(true);
  const [authGraceExpired, setAuthGraceExpired] = useState(false);
  const [offerMode, setOfferMode] = useState<OfferMode>("trial");
  const [userIntentSegment, setUserIntentSegment] = useState<UserIntentSegment>("cold_user");
  const [isSegmentResolved, setIsSegmentResolved] = useState(false);
  const [hasExternalOfferOverride, setHasExternalOfferOverride] = useState(false);
  const [hasAppliedSegmentDefault, setHasAppliedSegmentDefault] = useState(false);
  const [externalCouponProcessed, setExternalCouponProcessed] = useState(false);
  const [lastDiscountCode, setLastDiscountCode] = useState("birdsofafeather");
  const [offerings, setOfferings] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [currency, setCurrency] = useState<Currency>("USD");
  const [coupon, setCoupon] = useState("");
  const [couponApplied, setCouponApplied] = useState(false);
  const [selectedOfferingKey, setSelectedOfferingKey] = useState<string | null>(null);
  const [showCouponInput, setShowCouponInput] = useState(false);
  const [showOfferToggleOnPromo, setShowOfferToggleOnPromo] = useState(false);
  const [selectedBilling, setSelectedBilling] = useState<PackageType>("monthly");

  // Detect user currency
  useEffect(() => {
    async function detectCurrency() {
      try {
        const detectedCurrency = await detectUserCurrency();
        setCurrency(detectedCurrency);
        logger.info("Currency detected", { currency: detectedCurrency });
      } catch (err) {
        logger.warn("Failed to detect currency, using USD", { error: err });
        setCurrency("USD");
      }
    }
    detectCurrency();
  }, []);

  useEffect(() => {
    if (authLoading || user) {
      setAuthGraceExpired(false);
      return;
    }

    const hasStoredSession = fastAuthCheck();
    if (!hasStoredSession) {
      setAuthGraceExpired(true);
      return;
    }

    logger.info("Paywall waiting for client auth hydration", {
      hasStoredSession,
    });

    const timeoutId = window.setTimeout(() => {
      logger.warn("Paywall auth hydration grace window expired");
      setAuthGraceExpired(true);
    }, 1500);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [authLoading, user]);

  // Initialize RevenueCat
  useEffect(() => {
    async function initialize() {
      const hasStoredSession = fastAuthCheck();
      const authDecision = resolvePaywallAuthGateDecision({
        authLoading,
        user,
        hasStoredSession,
        authGraceExpired,
      });

      logger.info("Paywall auth gate evaluated", {
        authLoading,
        hasUser: !!user,
        hasStoredSession,
        authGraceExpired,
        authDecision,
      });

      if (authDecision === "wait") {
        return;
      }

      if (authDecision === "redirect" || !user) {
        logger.warn("Redirecting unauthenticated paywall visitor to auth", {
          hasStoredSession,
          authGraceExpired,
        });
        const paywallPath = `/paywall${searchParams?.toString() ? `?${searchParams.toString()}` : ""}`;
        router.push(`/auth?redirect=${encodeURIComponent(paywallPath)}`);
        return;
      }

      try {
        // Always use email as the app user ID for RevenueCat
        const userId = user.email;

        if (!userId) {
          throw new Error("No email available for RevenueCat initialization");
        }

        logger.info("Initializing RevenueCat", {
          appUserId: userId,
          userIdFromAuth: user.id,
          detectedCurrency: currency,
        });

        await initializeRevenueCat(userId);
        const fetchedOfferings = await getOfferings();
        setOfferings(fetchedOfferings);

        logger.info("Paywall initialized", {
          userId,
          currency,
          offeringsCount: fetchedOfferings.all ? Object.keys(fetchedOfferings.all).length : 0,
          hasCurrentOffering: !!fetchedOfferings.current,
          currentOfferingId: fetchedOfferings.current?.identifier,
          availablePackagesCount: fetchedOfferings.current?.availablePackages?.length || 0,
          packageIdentifiers:
            fetchedOfferings.current?.availablePackages?.map((p: any) => p.identifier) || [],
        });
      } catch (err: any) {
        logger.error("Failed to initialize paywall", { error: err });
        setError(getPaywallLoadErrorMessage());
      } finally {
        setIsInitializing(false);
      }
    }

    initialize();
  }, [user, authLoading, router, currency, authGraceExpired]);

  function findDiscountOfferingKey(
    all: Record<string, any> | undefined | null,
    couponCode: string
  ) {
    if (!all) return null;
    const code = couponCode.toLowerCase();
    const entries = Object.entries(all);

    // Any coupon ending in 33 uses the discount offering
    if (code.endsWith("33")) {
      for (const [key, off] of entries) {
        const id = (off?.identifier ?? "").toString();
        if (id === "botes33_offering" || id === "ofrng3dc99cd0fa") {
          return key;
        }
      }
      // Fallback: match any offering that clearly represents a 33% promo.
      for (const [key, off] of entries) {
        const id = (off?.identifier ?? "").toString().toLowerCase();
        const desc = (off?.serverDescription ?? "").toString().toLowerCase();
        if (id.includes("33") || desc.includes("33")) {
          return key;
        }
      }
      return null;
    }

    // Birdsofafeather logic (default)
    for (const [key, off] of entries) {
      const id = (off?.identifier ?? "").toString();
      const desc = (off?.serverDescription ?? "").toString();
      if (
        id.toLowerCase() === "chicky_subscription_20_off" ||
        id.toLowerCase().includes("20_off") ||
        id.toLowerCase().includes("20% off") ||
        desc.toLowerCase().includes("20% off") ||
        id === "ofrng3050721c6b"
      ) {
        return key;
      }
    }
    return null;
  }

  function getActiveOffering() {
    if (!offerings) return null;
    if (couponApplied && selectedOfferingKey && offerings.all?.[selectedOfferingKey]) {
      return offerings.all[selectedOfferingKey];
    }
    return offerings.current ?? null;
  }

  function resetToTrialMode() {
    setOfferMode("trial");
    setCoupon("");
    setCouponApplied(false);
    setSelectedOfferingKey(null);
  }

  async function applyCoupon(overrideCode?: string): Promise<boolean> {
    setError(null);
    const code = (overrideCode ?? coupon).trim().toLowerCase();

    if (overrideCode) {
      setCoupon(overrideCode);
    }

    // Accept any coupon ending in 33, or birdsofafeather or savestate
    if (code !== "birdsofafeather" && code !== "savestate" && !code.endsWith("33")) {
      setCouponApplied(false);
      setSelectedOfferingKey(null);
      setError("That code isn't valid. Check it and try again.");
      return false;
    }

    if (!offerings) {
      setError("The plans are still loading. Please try again in a moment.");
      return false;
    }

    const key = findDiscountOfferingKey(offerings.all, code);
    if (!key) {
      setError("That discount isn't available right now. Choose another plan or contact support.");
      return false;
    }

    setSelectedOfferingKey(key);
    setCouponApplied(true);
    setLastDiscountCode(code);

    return true;
  }

  // Handle coupon from URL parameter or Cookie
  useEffect(() => {
    const couponFromUrl = searchParams?.get("coupon");
    let couponToApply = couponFromUrl;

    // If no URL coupon, check cookie
    if (!couponToApply && typeof document !== "undefined") {
      const match = document.cookie.match(new RegExp("(^| )coupon_code=([^;]+)"));
      if (match) {
        couponToApply = match[2];
      }
    }

    const normalizedCoupon = (() => {
      if (!couponToApply) return null;
      let value = couponToApply;
      try {
        value = decodeURIComponent(value);
      } catch {
        // Keep raw value if decoding fails.
      }
      const trimmed = value.trim();
      return trimmed || null;
    })();

    if (normalizedCoupon) {
      setHasExternalOfferOverride(true);
      setHasAppliedSegmentDefault(true);
    }

    if (normalizedCoupon && !couponApplied && !externalCouponProcessed) {
      setCoupon(normalizedCoupon);
      setShowCouponInput(true);
      logger.info("Coupon detected", {
        coupon: normalizedCoupon,
        source: couponFromUrl ? "url" : "cookie",
      });

      // Auto-apply if it's a valid coupon (ends in 33 or is birdsofafeather or savestate)
      const urlCode = normalizedCoupon.toLowerCase();
      const isValidCoupon =
        urlCode === "birdsofafeather" || urlCode === "savestate" || urlCode.endsWith("33");

      if (!isValidCoupon) {
        setExternalCouponProcessed(true);
        return;
      }

      // Wait for offerings to load before marking coupon processing complete.
      if (!offerings) return;

      setExternalCouponProcessed(true);
      void applyCoupon(normalizedCoupon).then((applied) => {
        setOfferMode(applied ? "discount" : "trial");
      });
    }
  }, [searchParams, couponApplied, offerings, externalCouponProcessed]);

  // Segment user intent from Supabase-backed conversation stats.
  useEffect(() => {
    let isCancelled = false;

    async function resolveSegment() {
      if (authLoading || !user) return;

      try {
        const response = await fetch("/api/progress/conversations-stats", {
          method: "GET",
          credentials: "include",
          cache: "no-store",
        });

        if (!response.ok) {
          throw new Error(`stats_http_${response.status}`);
        }

        const stats = await response.json();
        const segment = classifyUserIntentFromStats(stats);
        if (!isCancelled) {
          setUserIntentSegment(segment);
          logger.info("Paywall user segment resolved", {
            segment,
            sessions: stats?.sessions,
            sessionsThisWeek: stats?.sessionsThisWeek,
            totalMessages: stats?.totalMessages,
            lastActivityAt: stats?.lastActivityAt,
          });
        }
      } catch (err) {
        if (!isCancelled) {
          setUserIntentSegment("cold_user");
          logger.warn("Falling back to cold_user segment", { error: err });
        }
      } finally {
        if (!isCancelled) {
          setIsSegmentResolved(true);
        }
      }
    }

    resolveSegment();

    return () => {
      isCancelled = true;
    };
  }, [authLoading, user]);

  // Auto-select default offer mode after segment resolution unless coupon is externally forced.
  useEffect(() => {
    if (!isSegmentResolved || !offerings || hasAppliedSegmentDefault || hasExternalOfferOverride) {
      return;
    }

    const recommendedMode = recommendedOfferModeForSegment(userIntentSegment);

    if (recommendedMode === "trial") {
      resetToTrialMode();
      setHasAppliedSegmentDefault(true);
      return;
    }

    void applyCoupon(lastDiscountCode).then((applied) => {
      setOfferMode(applied ? "discount" : "trial");
      setHasAppliedSegmentDefault(true);
    });
  }, [
    isSegmentResolved,
    offerings,
    hasAppliedSegmentDefault,
    hasExternalOfferOverride,
    userIntentSegment,
    lastDiscountCode,
  ]);

  useEffect(() => {
    const paywallViewOwner = user?.id ?? "guest";
    if (trackedPaywallViewOwnerRef.current === paywallViewOwner || authLoading || !offerings) {
      return;
    }

    if (user && !isSegmentResolved) {
      return;
    }

    if (!isZenMode && paywallExperiment.status !== "resolved") {
      return;
    }

    if (isGuidedAnchorTreatment && !preferencesInitialized) {
      return;
    }

    trackedPaywallViewOwnerRef.current = paywallViewOwner;
    trackPaywallView({
      surface: "paywall_page",
      source: paywallSource,
      userType: user ? "authenticated" : "guest",
      offerMode,
      userIntentSegment,
    });
    trackEventWithAttribution(posthog, "paywall_view", {
      offer_mode: offerMode,
      paywall_source: paywallSource,
      source: paywallSource,
      surface: "paywall_page",
      user_intent_segment: userIntentSegment,
      user_type: user ? "authenticated" : "guest",
      ...getWebappSignupBeforeDemoAttributionProperties(),
    });
    const hasValidExperimentExposure =
      !isZenMode &&
      paywallExperiment.source === "posthog" &&
      exposePaywallGuidedAnchorExperiment(posthog, paywallExperiment.variant);

    if (hasValidExperimentExposure) {
      trackEventWithAttribution(posthog, "paywall_experiment_exposed", {
        experiment_key: PAYWALL_GUIDED_ANCHOR_EXPERIMENT_KEY,
        experiment_variant: paywallExperiment.variant,
        offer_mode: offerMode,
        paywall_layout: paywallLayout,
        paywall_source: paywallSource,
        source: paywallSource,
        user_intent_segment: userIntentSegment,
      });
    }
    if (isGuidedAnchorTreatment && hasValidExperimentExposure) {
      trackEventWithAttribution(posthog, "guided_anchor_paywall_viewed", {
        default_billing_frequency: "monthly",
        experiment_key: PAYWALL_GUIDED_ANCHOR_EXPERIMENT_KEY,
        experiment_variant: paywallExperiment.variant,
        offer_mode: offerMode,
        paywall_layout: paywallLayout,
        paywall_source: paywallSource,
        source: paywallSource,
        user_intent_segment: userIntentSegment,
      });
    }
  }, [
    authLoading,
    offerings,
    user,
    isSegmentResolved,
    offerMode,
    userIntentSegment,
    isZenMode,
    isGuidedAnchorTreatment,
    paywallExperiment,
    paywallLayout,
    paywallSource,
    posthog,
    preferencesInitialized,
  ]);

  useEffect(() => {
    if (authLoading || !user || !offerings || !isSegmentResolved) return;

    if (recoveryRecordOwnerRef.current !== user.id) {
      writePendingPaywallRecovery(
        createPaywallRecoveryRecord({
          couponApplied,
          offerMode,
          ownerUserId: user.id,
          returnTo: searchParams?.get("return_to"),
          source: paywallSource,
          userIntentSegment,
        })
      );
      recoveryRecordOwnerRef.current = user.id;
      return;
    }

    updatePendingPaywallRecovery({ couponApplied, offerMode });
  }, [
    authLoading,
    couponApplied,
    isSegmentResolved,
    offerMode,
    offerings,
    paywallSource,
    searchParams,
    user,
    userIntentSegment,
  ]);

  const handleSwitchToTrial = () => {
    setHasAppliedSegmentDefault(true);
    resetToTrialMode();
  };

  const handleSwitchToDiscount = async () => {
    setHasAppliedSegmentDefault(true);
    const applied = await applyCoupon(lastDiscountCode);
    setOfferMode(applied ? "discount" : "trial");
  };

  const handlePaywallExit = () => {
    const returnTo = sanitizeInternalReturnTo(searchParams?.get("return_to"));
    let recovery = updatePendingPaywallRecovery({
      couponApplied,
      exitEventTracked: true,
      exitMethod: "back_button",
      offerMode,
    });

    if (!recovery) {
      if (!user?.id) {
        router.push(returnTo);
        return;
      }
      recovery = createPaywallRecoveryRecord({
        couponApplied,
        offerMode,
        ownerUserId: user.id,
        returnTo,
        source: paywallSource,
        userIntentSegment,
      });
      recovery = { ...recovery, exitEventTracked: true, exitMethod: "back_button" };
      writePendingPaywallRecovery(recovery);
    }

    trackEventWithAttribution(posthog, "paywall_exit_detected", {
      paywall_source: paywallSource,
      source: recovery.source,
      exit_method: recovery.exitMethod,
      offer_mode: recovery.offerMode,
      user_intent_segment: recovery.userIntentSegment,
      coupon_applied: recovery.couponApplied,
      return_route_category: getReturnRouteCategory(recovery.returnTo),
    });
    router.push(returnTo);
  };

  const buildCheckoutSuccessPath = (source: "purchase" | "purchase_recovery") => {
    const successParams = new URLSearchParams({ source });
    const schoolReturn = schoolCheckoutReturnTo(
      window.location.pathname,
      searchParams?.get("return_to")
    );
    if (schoolReturn) successParams.set("return_to", schoolReturn);
    if (paywallSource === "guest_demo_minute_five_choice") {
      successParams.set("origin", "guest_demo_minute_five_choice");
      successParams.set("return_to", sanitizeInternalReturnTo(searchParams?.get("return_to")));
    }
    return schoolHref(`/success?${successParams.toString()}`, schoolReturn);
  };

  const completeRecoveredCheckout = (attempt: CheckoutAttempt) => {
    if (!checkout.finishAttempt(attempt.id, "succeeded")) return false;
    if (user?.email) {
      invalidateRevenueCatSubscriptionCache(user.email);
    }
    clearPendingPaywallRecovery();
    router.push(buildCheckoutSuccessPath("purchase_recovery"));
    return true;
  };

  const handleCheckoutPending = (attempt: CheckoutAttempt) => {
    setError(null);
    void checkout.handlePendingPurchase(attempt).then((resolution) => {
      if (resolution.status === "active") {
        completeRecoveredCheckout(attempt);
      } else if (resolution.message) {
        setError(resolution.message);
      }
    });
  };

  const handleSelectPackage = async (packageType: PackageType) => {
    if (!user?.email) {
      const paywallPath = `/paywall${searchParams?.toString() ? `?${searchParams.toString()}` : ""}`;
      router.push(`/auth?redirect=${encodeURIComponent(paywallPath)}`);
      return;
    }

    const attempt = checkout.beginAttempt({
      packageType,
      offerMode,
      userIntentSegment,
      surface: "paywall_page",
      analyticsProperties: { paywall_source: paywallSource },
    });
    if (!attempt) return;

    let stage: CheckoutStage = "package_validation";
    setError(null);

    try {
      // Track upgrade intent
      trackUpgradeIntent(posthog, "paywall_page", {
        user_id: user.id,
        email: user.email,
        package: packageType,
        offer_mode: offerMode,
        paywall_source: paywallSource,
        user_intent_segment: userIntentSegment,
      });

      logger.info("Initiating purchase", { packageType, email: user.email });

      // Get the package from the active offering (discount if applied)
      const activeOffering = getActiveOffering();
      if (!activeOffering) {
        throw new Error("No offerings available. Please refresh the page.");
      }

      const packageIdentifier = REVENUECAT_CONFIG.packages[packageType].identifier;
      const packageToPurchase = findSelectableRevenueCatPackage({
        activeOffering,
        coupon,
        couponApplied,
        offerings,
        packageType,
        preferredCurrency: currency,
      });

      if (!packageToPurchase) {
        logger.error("Package not found in offerings", {
          packageType,
          packageIdentifier,
          availablePackages: activeOffering.availablePackages?.map((p: any) => p.identifier),
        });
        throw new Error(`Package ${packageType} not found. Please contact support.`);
      }

      logger.info("Found package, initiating purchase", {
        packageIdentifier,
        packageType,
        usingDiscount: couponApplied,
        offeringKey: couponApplied ? selectedOfferingKey : offerings?.current?.identifier,
        couponCode: couponApplied ? coupon : undefined,
      });

      const checkoutValue =
        getRevenueCatPackagePriceAmount(packageToPurchase) ??
        getFallbackPackagePriceValue(packageType, currency);
      const checkoutCurrency = getRevenueCatPackageCurrency(packageToPurchase, currency);
      trackBeginCheckout({
        value: checkoutValue,
        currency: checkoutCurrency,
        surface: "paywall_page",
        offerMode,
        paywallSource,
        userType: "authenticated",
        items: [
          {
            itemId:
              packageToPurchase?.webBillingProduct?.identifier ||
              packageToPurchase?.rcBillingProduct?.identifier ||
              packageToPurchase?.product?.identifier ||
              packageIdentifier,
            itemName: REVENUECAT_CONFIG.packages[packageType].displayName,
            price: checkoutValue,
          },
        ],
      });

      // Prepare metadata for RevenueCat
      const metadata: Record<string, string> = {
        checkout_attempt_id: attempt.id,
        checkout_telemetry_version: String(CHECKOUT_TELEMETRY_VERSION),
        paywall_source: paywallSource,
        ...buildOpenAIAdsPurchaseMetadata(),
      };

      if (couponApplied && coupon) {
        metadata.coupon_code = coupon.toLowerCase();
      }

      // Set PostHog ID if available
      let posthogUserId: string | undefined;
      try {
        const posthogId = posthog.get_distinct_id();
        if (posthogId) {
          posthogUserId = posthogId;
        }
      } catch (e) {
        logger.warn("Failed to get PostHog ID for attributes", { error: e });
      }

      const attributes = buildCheckoutSubscriberAttributes({
        email: user.email,
        posthogUserId,
        couponCode: couponApplied ? coupon : undefined,
      });

      // Set attributes before purchase
      stage = "attributes";
      if (Object.keys(attributes).length > 0) {
        await setAttributes(attributes);
      }

      // Purchase the package using RevenueCat SDK
      // Pass email to skip email collection step (faster checkout)
      stage = "sdk_purchase";
      checkout.startPurchase(attempt);
      const result = await purchasePackage(packageToPurchase, metadata, user.email, {
        onStalled: () => handleCheckoutPending(attempt),
      });

      if (!checkout.finishSuccessfulPurchase(attempt)) return;

      logger.info("Purchase successful", {
        packageType,
        activeSubscriptions: result.customerInfo.activeSubscriptions,
        couponCode: couponApplied ? coupon : undefined,
      });
      invalidateRevenueCatSubscriptionCache(user.email);

      trackRevenueCatConversionEvent({
        currency: checkoutCurrency,
        isTrial: offerMode === "trial" && packageType !== "weekly",
        itemId:
          packageToPurchase?.webBillingProduct?.identifier ||
          packageToPurchase?.rcBillingProduct?.identifier ||
          packageToPurchase?.product?.identifier ||
          packageIdentifier,
        itemName: REVENUECAT_CONFIG.packages[packageType].displayName,
        packageId: packageToPurchase?.identifier,
        paywallSource,
        source: "paywall_page",
        transactionId: `rc_client_${attempt.id}`,
        userEmail: user.email,
        value:
          offerMode === "trial" && packageType !== "weekly"
            ? 0
            : (getRevenueCatPackagePriceAmount(packageToPurchase) ??
              getFallbackPackagePriceValue(packageType, currency)),
      });

      // Redirect to success page, preserving only the already-sanitized speaking return route.
      clearPendingPaywallRecovery();
      router.push(buildCheckoutSuccessPath("purchase"));
    } catch (err: unknown) {
      const resolution = await checkout.handlePurchaseError(attempt, stage, err);
      if (resolution.status === "active") {
        completeRecoveredCheckout(attempt);
      } else if (resolution.message) {
        setError(resolution.message);
      }
    }
  };

  const handleVerifyStalledCheckout = async () => {
    const attempt = checkout.getCurrentAttempt();
    if (!attempt) return;

    const resolution = await checkout.verifyCurrentAttempt();
    if (resolution.status === "active") {
      completeRecoveredCheckout(attempt);
    } else if (resolution.message) {
      setError(resolution.message);
    }
  };

  const activeOffering = getActiveOffering();
  const weeklyPricing = getPackageDisplayPricing({
    currency,
    discountRequested: couponApplied,
    packageToPurchase: findSelectableRevenueCatPackage({
      activeOffering,
      coupon,
      couponApplied,
      offerings,
      packageType: "weekly",
      preferredCurrency: currency,
    }),
    packageType: "weekly",
  });
  const monthlyPricing = getPackageDisplayPricing({
    currency,
    discountRequested: couponApplied,
    packageToPurchase: findSelectableRevenueCatPackage({
      activeOffering,
      coupon,
      couponApplied,
      offerings,
      packageType: "monthly",
      preferredCurrency: currency,
    }),
    packageType: "monthly",
  });
  const annualPricing = getPackageDisplayPricing({
    currency,
    discountRequested: couponApplied,
    packageToPurchase: findSelectableRevenueCatPackage({
      activeOffering,
      coupon,
      couponApplied,
      offerings,
      packageType: "annual",
      preferredCurrency: currency,
    }),
    packageType: "annual",
  });
  const hasVisibleDiscount =
    weeklyPricing.hasDiscountPrice ||
    monthlyPricing.hasDiscountPrice ||
    annualPricing.hasDiscountPrice;
  const promoDisplay = getPromoDisplayState({
    couponApplied,
    hasVisibleDiscount,
    showOfferToggle: showOfferToggleOnPromo,
  });
  const pricingByPackage = {
    weekly: weeklyPricing,
    monthly: monthlyPricing,
    annual: annualPricing,
  } satisfies Record<PackageType, typeof weeklyPricing>;
  const annualSavingsPercent = getComparableAnnualSavingsPercent({
    annualPricing,
    fallbackCurrency: currency,
    monthlyPricing,
  });
  const reservationFirstName =
    typeof user?.user_metadata?.first_name === "string"
      ? user.user_metadata.first_name
      : typeof user?.user_metadata?.full_name === "string"
        ? user.user_metadata.full_name.split(" ")[0]
        : null;

  if (
    authLoading ||
    isInitializing ||
    (!isZenMode && paywallExperiment.status === "loading") ||
    (isGuidedAnchorTreatment && !preferencesInitialized)
  ) {
    return (
      <PaywallSkeleton layout={!isZenMode && !isGuidedAnchorTreatment ? "neutral" : "offers"} />
    );
  }

  return (
    <div
      className={`min-h-screen ${
        isZenMode
          ? "bg-[#09090b] selection:bg-stone-500/30"
          : "bg-[#0F0F14] selection:bg-[#FD954F]/30"
      } text-white transition-colors duration-500`}
      data-paywall-variant={isZenMode ? "zen" : paywallExperiment.variant}
    >
      {isZenMode && (
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute right-0 top-0 h-[500px] w-[500px] rounded-full bg-stone-500/5 blur-3xl" />
        </div>
      )}

      <div className="relative max-w-7xl mx-auto px-4 py-12">
        {/* Back Button */}
        <button
          onClick={handlePaywallExit}
          className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-8"
        >
          <ArrowLeft className="w-5 h-5" />
          Back
        </button>

        {isZenMode ? (
          <div className="mx-auto mb-12 hidden max-w-5xl px-4 text-center md:block md:mb-20">
            <motion.h1
              animate={{ opacity: 1, y: 0 }}
              className="mb-4 font-serif text-4xl font-bold leading-tight tracking-tight text-white md:mb-6 md:text-6xl lg:text-7xl"
              initial={{ opacity: 0, y: -20 }}
            >
              Deepen your practice.
              <br />
              <span className="italic text-stone-400">Expand your mind.</span>
            </motion.h1>
            <motion.p
              animate={{ opacity: 1, y: 0 }}
              className="mx-auto mb-6 max-w-3xl text-base leading-relaxed text-gray-300 md:text-lg lg:text-xl"
              initial={{ opacity: 0, y: -10 }}
              transition={{ delay: 0.1 }}
            >
              Your personal AI meditation guide. Access unlimited sessions, deeper conversations,
              and advanced mindfulness practices.
            </motion.p>
          </div>
        ) : (
          <motion.header
            animate={{ opacity: 1, y: 0 }}
            className="mx-auto mb-10 max-w-3xl text-center md:mb-12"
            initial={{ opacity: 0, y: -10 }}
          >
            <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#FD954F]">
              {isGuidedAnchorTreatment ? "Choose your level of support" : "AI speaking membership"}
            </p>
            <h1 className="mt-3 text-3xl font-extrabold tracking-tight text-white md:text-5xl">
              {isGuidedAnchorTreatment ? "Choose how you want to learn" : "Choose your plan"}
            </h1>
            <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-[#AEAEB2] md:text-lg">
              {isGuidedAnchorTreatment
                ? "Practise independently with AI, or join six live online classes led by a human teacher."
                : "Every plan includes unlimited speaking practice in 70+ languages. Choose how often you want to pay."}
            </p>
          </motion.header>
        )}

        {/* Error Message */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl mx-auto mb-8 p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-300 text-center"
          >
            {error}
          </motion.div>
        )}

        {(checkout.status === "pending_unconfirmed" ||
          checkout.status === "verifying" ||
          checkout.status === "unconfirmed") && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mx-auto mb-8 max-w-2xl rounded-lg border border-amber-400/40 bg-amber-400/10 p-4 text-center text-amber-100"
            role="status"
          >
            <p>
              Your checkout is taking longer than usual. Please keep it open and don&apos;t start
              another purchase.
            </p>
            <button
              type="button"
              onClick={handleVerifyStalledCheckout}
              disabled={checkout.status === "verifying"}
              className="mt-3 rounded-md border border-amber-300/40 px-4 py-2 text-sm font-semibold transition-colors hover:bg-amber-300/10 disabled:opacity-60"
            >
              {checkout.status === "verifying" ? "Checking…" : "Check purchase status"}
            </button>
          </motion.div>
        )}

        {isZenMode && (
          <motion.div
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 text-center"
            initial={{ opacity: 0, y: -10 }}
            transition={{ delay: 0.4 }}
          >
            <h2 className="mb-3 font-serif text-2xl font-bold text-white md:text-3xl lg:text-4xl">
              Continue Your Journey
            </h2>
            <p className="text-base text-gray-400 md:text-lg">
              Unlock unlimited Zen sessions and premium features
            </p>
          </motion.div>
        )}

        {/* Pricing Cards */}
        {isZenMode ? (
          <div className="mx-auto mb-12 grid max-w-6xl grid-cols-1 gap-8 px-4 md:mb-16 md:grid-cols-3 md:gap-8">
            <PricingCard
              packageType="weekly"
              currency={currency}
              onSelectAction={handleSelectPackage}
              disabled={checkout.locked}
              isLoading={
                checkout.selectedPackage === "weekly" &&
                (checkout.status === "preparing" || checkout.status === "awaiting_checkout")
              }
              isSelected={checkout.selectedPackage === "weekly"}
              showFreeTrial={false}
              overridePriceFormatted={weeklyPricing.priceFormatted}
              originalPriceFormatted={weeklyPricing.originalPriceFormatted}
              discountLabel={weeklyPricing.hasDiscountPrice ? "Discount applied" : undefined}
              promotionalPeriod={weeklyPricing.promotionalPeriod}
              variant="zen"
            />
            <PricingCard
              packageType="monthly"
              currency={currency}
              onSelectAction={handleSelectPackage}
              disabled={checkout.locked}
              isLoading={
                checkout.selectedPackage === "monthly" &&
                (checkout.status === "preparing" || checkout.status === "awaiting_checkout")
              }
              isSelected={checkout.selectedPackage === "monthly"}
              showFreeTrial={offerMode === "trial" && monthlyPricing.showFreeTrial}
              overridePriceFormatted={monthlyPricing.priceFormatted}
              originalPriceFormatted={monthlyPricing.originalPriceFormatted}
              discountLabel={monthlyPricing.hasDiscountPrice ? "Discount applied" : undefined}
              promotionalPeriod={monthlyPricing.promotionalPeriod}
              variant="zen"
            />
            <PricingCard
              packageType="annual"
              currency={currency}
              onSelectAction={handleSelectPackage}
              disabled={checkout.locked}
              isLoading={
                checkout.selectedPackage === "annual" &&
                (checkout.status === "preparing" || checkout.status === "awaiting_checkout")
              }
              isSelected={checkout.selectedPackage === "annual"}
              showFreeTrial={offerMode === "trial" && annualPricing.showFreeTrial}
              overridePriceFormatted={annualPricing.priceFormatted}
              originalPriceFormatted={annualPricing.originalPriceFormatted}
              discountLabel={annualPricing.hasDiscountPrice ? "Discount applied" : undefined}
              promotionalPeriod={annualPricing.promotionalPeriod}
              variant="zen"
            />
          </div>
        ) : (
          <div className="mb-10 md:mb-12">
            <PaywallExperimentPricingCards
              analyticsSource="paywall"
              annualSavingsPercent={annualSavingsPercent}
              checkoutLocked={checkout.locked}
              checkoutStatus={checkout.status}
              cohortEmail={user?.email ?? ""}
              cohortFirstName={reservationFirstName}
              cohortLanguageCode={preferences.selectedLanguageCode}
              offerMode={offerMode}
              onBillingChange={(packageType) => {
                setSelectedBilling(packageType);
                trackEventWithAttribution(posthog, "paywall_billing_frequency_selected", {
                  billing_frequency: packageType,
                  experiment_key: PAYWALL_GUIDED_ANCHOR_EXPERIMENT_KEY,
                  experiment_variant: paywallExperiment.variant,
                  offer_mode: offerMode,
                  paywall_layout: paywallLayout,
                  source: "paywall",
                  user_intent_segment: userIntentSegment,
                });
              }}
              onPurchase={handleSelectPackage}
              pricingByPackage={pricingByPackage}
              selectedBilling={selectedBilling}
              selectedPackage={checkout.selectedPackage}
              variant={paywallExperiment.variant}
            />
          </div>
        )}

        {/* Promo code */}
        {promoDisplay.showSection && (
          <div className={!isZenMode ? "mb-10 mt-6 text-center" : "mb-12 mt-8 text-center"}>
            {!couponApplied && (
              <button
                disabled={checkout.locked}
                onClick={() => {
                  setShowOfferToggleOnPromo(true);
                  setShowCouponInput((prev) => !prev);
                }}
                className={`text-sm font-medium transition-colors ${
                  !isZenMode
                    ? "text-[#AEAEB2] hover:text-[#FD954F]"
                    : "text-gray-400 hover:text-orange-400"
                }`}
              >
                Have a promo code?
              </button>
            )}
            {promoDisplay.showConfirmation &&
              (!isZenMode ? (
                <p className="text-sm font-semibold text-emerald-300">
                  Limited-time promotional pricing applied
                </p>
              ) : (
                <div className="inline-block rounded-lg border border-green-500/50 bg-green-500/20 px-4 py-2">
                  <span className="text-sm font-semibold text-green-400">
                    Limited-time promotional pricing applied
                  </span>
                </div>
              ))}

            {showOfferToggleOnPromo && (
              <div className="mt-4 flex flex-wrap justify-center gap-2">
                <button
                  disabled={checkout.locked}
                  onClick={handleSwitchToTrial}
                  className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    offerMode === "trial"
                      ? !isZenMode
                        ? "bg-[#FD954F] text-[#1C1C1E]"
                        : "bg-orange-600 text-white"
                      : "bg-gray-800 text-gray-300 hover:text-white"
                  }`}
                >
                  3-Day Trial
                </button>
                <button
                  disabled={checkout.locked}
                  onClick={() => void handleSwitchToDiscount()}
                  className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    offerMode === "discount"
                      ? "bg-green-600 text-white"
                      : "bg-gray-800 text-gray-300 hover:text-white"
                  }`}
                >
                  Up to 33% Off
                </button>
              </div>
            )}

            {/* Expandable Coupon Input */}
            {showCouponInput && !couponApplied && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
                className="mt-4 flex gap-2 justify-center max-w-sm mx-auto"
              >
                <input
                  disabled={checkout.locked}
                  type="text"
                  value={coupon}
                  onChange={(e) => setCoupon(e.target.value)}
                  placeholder="Enter promo code"
                  className={`flex-1 rounded-lg border px-3 py-2 text-sm text-white focus:outline-none ${
                    !isZenMode
                      ? "border-white/10 bg-[#15161B] placeholder-[#8E8E93] focus:border-[#FD954F]"
                      : "border-gray-700/60 bg-gray-800/60 placeholder-gray-500 focus:border-orange-500"
                  }`}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      void applyCoupon().then((applied) => {
                        setOfferMode(applied ? "discount" : "trial");
                      });
                    }
                  }}
                />
                <button
                  disabled={checkout.locked}
                  onClick={() =>
                    void applyCoupon().then((applied) => {
                      setOfferMode(applied ? "discount" : "trial");
                    })
                  }
                  className={`rounded-lg px-4 py-2 text-sm font-semibold text-white transition-colors ${
                    !isZenMode
                      ? "bg-[#FD954F] hover:bg-[#F28747]"
                      : "bg-orange-600 hover:bg-orange-700"
                  }`}
                >
                  Apply
                </button>
              </motion.div>
            )}
          </div>
        )}
      </div>

      {/* Testimonials Section - Hidden in Zen Mode for minimalism */}
      {!isZenMode && (
        <div className="mb-12 md:mb-16">
          <InfiniteTestimonialScroll testimonials={guidedAnchorTestimonials} />
        </div>
      )}

      <div className="relative max-w-7xl mx-auto px-4">
        {/* Footer */}
        <div className="text-center text-sm text-gray-500 pb-8 border-t border-gray-800 pt-8">
          <p className="mb-4 text-gray-400">
            {!isZenMode
              ? "Secure checkout · Available in 70+ languages"
              : "🔒 Secure checkout • 🌍 Available in 70+ languages"}
          </p>
          <div className="flex flex-wrap justify-center gap-4 md:gap-6 mb-4">
            <Link
              href="/"
              className={`transition-colors ${
                !isZenMode ? "hover:text-[#FD954F]" : "hover:text-orange-400"
              }`}
            >
              Home
            </Link>
            <Link
              href="/blog"
              className={`transition-colors ${
                !isZenMode ? "hover:text-[#FD954F]" : "hover:text-orange-400"
              }`}
            >
              Language Learning Blog
            </Link>
            <Link
              href="/terms"
              className={`transition-colors ${
                !isZenMode ? "hover:text-[#FD954F]" : "hover:text-orange-400"
              }`}
            >
              Terms & Conditions
            </Link>
          </div>
          <p className="text-xs text-gray-600">
            © {new Date().getFullYear()} ChickyTutor. Making language learning less painful, one
            conversation at a time.
          </p>
        </div>
      </div>
    </div>
  );
}

export default function PaywallPage() {
  return (
    <Suspense fallback={<PaywallSkeleton layout="neutral" />}>
      <PaywallContent />
    </Suspense>
  );
}
